import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import {
  useAiCheckout, useAiKeyRemove, useAiKeySave, useAiVerify, useAiWallet, useAiWalletUpdate,
} from '../../api/hooks';
import type { AiBundle, AiVerifyStatus } from '../../api/types';
import {
  Badge, Button, Callout, ConfirmDialog, EmptyState, ErrorPanel, FormField, Modal, PageHeader, PanelCard,
  SortableTh, Spinner, Switch, Table, Td, Th,
} from '../../components/ui';
import { apiErrorText } from '../../lib/apiError';
import { fieldClass } from '../../lib/fieldClass';
import { TH_RULE } from '../../lib/tableRules';
import { useSortableTable, type SortColumn } from '../../lib/useSortableTable';

const FEATURE_LABELS: Record<string, string> = {
  coach: 'Coach', coach_plain: 'Coach', exam_generation: 'Exam generation', exam_scorer: 'Exam checking',
  style_profile: 'Exam style', question_regenerate: 'Question rewrite', paper_import: 'Paper import',
  import_enrich: 'Import answers', worksheet_import: 'Worksheet import', written_marking: 'Written marking',
  parent_letter: 'Parent letters', weekly_summary: 'Weekly summaries', study_plan: 'Study plans',
  ne_tagging: 'National exam tagging', extract_questions: 'Question extraction', ai_generate: 'AI tools',
  ai_stream: 'AI tools', embedding: 'Search',
};
const featureLabel = (slug: string) => FEATURE_LABELS[slug] ?? slug;

const birr = (price: number | string) => {
  const n = Number(price);
  return `${Number.isInteger(n) ? n : n.toFixed(2)} Birr`;
};
const credits = (v: number | string) => Math.round(Number(v)).toLocaleString('en-US');
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

type FeatureRow = { feature: string; credits: number };
type StaffRow = { user_id: number; name: string; credits: number };
const FEATURE_COLS: SortColumn<FeatureRow>[] = [
  { key: 'feature', accessor: (r) => featureLabel(r.feature) },
  { key: 'credits', accessor: (r) => r.credits, type: 'number' },
];
const STAFF_COLS: SortColumn<StaffRow>[] = [
  { key: 'name', accessor: (r) => r.name },
  { key: 'credits', accessor: (r) => r.credits, type: 'number' },
];

function FeatureTable({ rows }: { rows: FeatureRow[] }) {
  const { rows: sorted, sort, toggle } = useSortableTable(rows, FEATURE_COLS, { key: 'credits', dir: 'desc' });
  return (
    <Table aria-label="Usage by feature" minWidth={320}>
      <thead><tr>
        <SortableTh columnKey="feature" sort={sort} onToggle={toggle} className={TH_RULE}>Feature</SortableTh>
        <SortableTh columnKey="credits" sort={sort} onToggle={toggle} align="right" className={TH_RULE}>Credits</SortableTh>
      </tr></thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.feature}><Td>{featureLabel(r.feature)}</Td><Td align="right" className="font-mono">{credits(r.credits)}</Td></tr>
        ))}
      </tbody>
    </Table>
  );
}

function StaffTable({ rows }: { rows: StaffRow[] }) {
  const { rows: sorted, sort, toggle } = useSortableTable(rows, STAFF_COLS, { key: 'name', dir: 'asc' });
  return (
    <Table aria-label="Usage by staff member" minWidth={320}>
      <thead><tr>
        <SortableTh columnKey="name" sort={sort} onToggle={toggle} className={TH_RULE}>Name</SortableTh>
        <SortableTh columnKey="credits" sort={sort} onToggle={toggle} align="right" className={TH_RULE}>Credits</SortableTh>
      </tr></thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.user_id}><Td className="font-semibold">{r.name}</Td><Td align="right" className="font-mono">{credits(r.credits)}</Td></tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Staff: school credit this month, personal credit with Chapa top-up, own OpenRouter key, purchases. */
export default function MyAiPage() {
  const q = useAiWallet();
  const navigate = useNavigate();
  const { hash, search, pathname } = useLocation();
  const checkout = useAiCheckout();
  const verify = useAiVerify();
  const keySave = useAiKeySave();
  const keyRemove = useAiKeyRemove();
  const update = useAiWalletUpdate();

  const [keyOpen, setKeyOpen] = useState(false);
  const [keyValue, setKeyValue] = useState('');
  const [keyError, setKeyError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);
  const [payment, setPayment] = useState<{ tx_ref: string; status: AiVerifyStatus } | null>(null);

  // Return from Chapa: verify once per tx_ref (a ref survives StrictMode's double effect), then clean the URL.
  const txRef = new URLSearchParams(search).get('tx_ref');
  const verified = useRef<string | null>(null);
  const runVerify = (ref: string) => {
    verify.mutate(ref, {
      onSuccess: (r) => setPayment({ tx_ref: ref, status: r.status }),
      onError: () => setPayment({ tx_ref: ref, status: 'pending' }),
    });
  };
  useEffect(() => {
    if (!txRef || verified.current === txRef) return;
    verified.current = txRef;
    runVerify(txRef);
    navigate({ pathname, hash }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txRef]);

  // Deep links from the AI-limit dialog.
  useEffect(() => {
    if (hash !== '#buy' && hash !== '#key') return;
    if (hash === '#key') setKeyOpen(true);
    document.getElementById(hash === '#key' ? 'ai-key' : 'ai-buy')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [hash, q.isSuccess]);

  const d = q.data;
  const purchasedCredits = payment ? d?.purchases.find((p) => p.tx_ref === payment.tx_ref)?.credits : undefined;

  const buy = (b: AiBundle) => {
    setBuyError(null);
    checkout.mutate(
      { bundle_id: b.id, return_url: `${window.location.origin}/settings/ai` },
      {
        onSuccess: (r) => window.location.assign(r.checkout_url),
        onError: (e) => setBuyError(apiErrorText(e, 'We could not start the payment. Please try again.')),
      },
    );
  };
  const closeKey = () => { setKeyOpen(false); setKeyValue(''); setKeyError(null); keySave.reset(); };
  const saveKey = () => {
    setKeyError(null);
    keySave.mutate(keyValue.trim(), {
      onSuccess: closeKey,
      onError: (e) => {
        setKeyValue('');
        setKeyError(apiErrorText(e, 'We could not save that key. Please try again.'));
        keySave.reset(); // drop the key (variables, error.config.data) from the mutation cache
      },
    });
  };

  const school = d?.school;
  const pct = school && school.allocation_credits > 0 ? Math.min(100, (school.used_credits / school.allocation_credits) * 100) : 0;
  const w = d?.wallet;
  const bundles = useMemo(() => d?.bundles ?? [], [d]);

  return (
    <div className="space-y-[18px]">
      <PageHeader title="My AI" subtitle="Your school's AI credit, your own credit, and your own OpenRouter key."
        trail={[{ label: 'Dashboard', to: '/dashboard' }]} />
      {q.isLoading ? <div className="flex justify-center py-10"><Spinner size="md" /></div>
        : q.isError || !d || !w ? <ErrorPanel />
        : (
          <>
            {payment && (payment.status === 'verified' || payment.status === 'already_verified') && (
              <Callout role="status" tone="info">
                {purchasedCredits ? `Payment received. ${credits(purchasedCredits)} credits added.` : 'Payment received.'}
              </Callout>
            )}
            {payment?.status === 'pending' && (
              <Callout role="status" tone="warm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>Payment not confirmed yet.</span>
                  <Button size="sm" variant="ghost" loading={verify.isPending} onClick={() => runVerify(payment.tx_ref)}>Check again</Button>
                </div>
              </Callout>
            )}
            {payment?.status === 'failed' && <Callout role="status" tone="warm">The payment did not go through.</Callout>}

            {school && (
              <PanelCard title="School AI credit this month"
                subtitle={`Resets on ${day(school.resets_on)}`}>
                <div role="progressbar" aria-label="School AI credit used" aria-valuemin={0} aria-valuemax={school.allocation_credits}
                  aria-valuenow={school.used_credits} className="h-2.5 w-full overflow-hidden rounded-full bg-note">
                  <div className={`h-full rounded-full ${school.exhausted ? 'bg-danger' : 'bg-accent'}`} style={{ width: `${pct}%` }} />
                </div>
                <p className={`mt-2 text-[13px] ${school.exhausted ? 'font-semibold text-danger' : 'text-muted'}`}>
                  {school.exhausted
                    ? `Used ${credits(school.used_credits)} of ${credits(school.allocation_credits)} credits. The school credit has ended for this month.`
                    : `Used ${credits(school.used_credits)} of ${credits(school.allocation_credits)} credits. ${credits(school.left_credits)} left.`}
                </p>
              </PanelCard>
            )}

            <div id="ai-buy">
              <PanelCard title="My credit"
                subtitle="Free AI models are slower and weaker in Amharic and Afaan Oromo. Your own credit is used after the school credit.">
                <p className="mb-4 font-display text-[28px] font-bold text-ink">{credits(w.balance_credits)} <span className="text-sm font-normal text-muted">credits left</span></p>
                {buyError && <Callout role="status" tone="warm" className="mb-3">{buyError}</Callout>}
                {d.purchases_enabled && bundles.length > 0 ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    {bundles.map((b) => (
                      <div key={b.id} className="rounded-[14px] border border-line p-4">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">{`${b.credits} credits · ${birr(b.price_etb)}`}</p>
                          {b.popular && <Badge tone="accent">Popular</Badge>}
                        </div>
                        <Button size="sm" className="mt-3" loading={checkout.isPending && checkout.variables?.bundle_id === b.id}
                          disabled={checkout.isPending} onClick={() => buy(b)}>Buy</Button>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-[13px] text-muted">Buying credit is not available right now.</p>}
              </PanelCard>
            </div>

            <div id="ai-key">
              <PanelCard title="My OpenRouter key"
                subtitle="Use your own OpenRouter account. Free models there are slower and weaker in Amharic and Afaan Oromo."
                actions={w.has_key ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setKeyOpen(true)}>Replace key</Button>
                    <Button size="sm" variant="danger-outline" onClick={() => setConfirmRemove(true)}>Remove</Button>
                  </>
                ) : <Button size="sm" onClick={() => setKeyOpen(true)}>Add key</Button>}>
                {w.has_key ? (
                  <div className="space-y-3">
                    <p className="text-sm text-ink">
                      Key ending {w.key_last4}{w.key_added_at ? `, added ${day(w.key_added_at)}` : ''}
                    </p>
                    <div className="flex items-center gap-3">
                      <Switch checked={w.use_own_key_first} label="Use my key before my credit" disabled={update.isPending}
                        onChange={(next) => update.mutate({ use_own_key_first: next })} />
                      <span aria-hidden="true" className="text-sm text-ink">Use my key before my credit</span>
                    </div>
                  </div>
                ) : <p className="text-[13px] text-muted">No key added yet.</p>}
              </PanelCard>
            </div>

            <PanelCard title="Recent purchases">
              {d.purchases.length === 0
                ? <EmptyState compact icon={<Sparkles size={18} aria-hidden />} title="No purchases yet" />
                : (
                  <Table minWidth={420}>
                    <thead><tr><Th className={TH_RULE}>Date</Th><Th className={TH_RULE} align="right">Credits</Th><Th className={TH_RULE} align="right">Amount</Th><Th className={TH_RULE}>Status</Th><Th className={TH_RULE}><span className="sr-only">Actions</span></Th></tr></thead>
                    <tbody>
                      {d.purchases.map((p) => (
                        <tr key={p.tx_ref}>
                          <Td>{day(p.created)}</Td>
                          <Td align="right" className="font-mono">{credits(p.credits)}</Td>
                          <Td align="right" className="font-mono">{birr(p.amount_etb)}</Td>
                          <Td className="capitalize">{p.status}</Td>
                          <Td align="right">
                            {p.status === 'pending' && (
                              <Button size="sm" variant="ghost" loading={verify.isPending && verify.variables === p.tx_ref}
                                disabled={verify.isPending} onClick={() => runVerify(p.tx_ref)}>Check</Button>
                            )}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
            </PanelCard>

            {d.breakdown && (
              <>
                <PanelCard title="This month by feature">
                  {d.breakdown.by_feature.length === 0
                    ? <p className="text-[13px] text-muted">No AI use yet this month.</p>
                    : <FeatureTable rows={d.breakdown.by_feature} />}
                </PanelCard>
                <PanelCard title="By staff member">
                  {d.breakdown.by_staff.length === 0
                    ? <p className="text-[13px] text-muted">No AI use yet this month.</p>
                    : <StaffTable rows={d.breakdown.by_staff} />}
                </PanelCard>
              </>
            )}
          </>
        )}

      <Modal isOpen={keyOpen} onClose={closeKey} title={w?.has_key ? 'Replace my OpenRouter key' : 'Add my OpenRouter key'} size="sm"
        footer={<>
          <Button variant="ghost" size="sm" onClick={closeKey}>Cancel</Button>
          <Button size="sm" loading={keySave.isPending} disabled={!keyValue.trim()} onClick={saveKey}>Save key</Button>
        </>}>
        <div className="space-y-3">
          <FormField label="OpenRouter key" htmlFor="ai-key-input" error={keyError ?? undefined}
            hint="Your key is stored safely and never shown again.">
            <input id="ai-key-input" type="password" autoComplete="off" className={fieldClass} value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)} />
          </FormField>
          <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="text-[13px] font-semibold text-primary underline">
            Get a key at openrouter.ai/keys
          </a>
        </div>
      </Modal>

      <ConfirmDialog isOpen={confirmRemove} title="Remove my OpenRouter key?" confirmLabel="Remove key" busy={keyRemove.isPending}
        body="AI will use your credit instead. You can add a key again any time."
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => keyRemove.mutate(undefined, { onSettled: () => setConfirmRemove(false) })} />
    </div>
  );
}
